<script lang="ts">
import { RouteNames } from "@/utils/constants.ts";

export function getRouteOnboardPersonalVerification() {
  return {
    name: RouteNames.ONBOARDING_PERSON_PROFILE_VERIFY
  };
}
</script>


<script setup lang="ts">
import snsWebSdk from "@sumsub/websdk";
import { onMounted, ref } from "vue";

import CardLayout from "@/components/layout/CardLayout.vue";
import { getRouteXDashboard } from "@/views/Auth/Dashboard.vue";
import { usePersonStore } from "@/store";
import PersonService from "@/services/personService.ts";
import { useDark } from "@vueuse/core";
import { IVerifyToken } from "@/repositories/dto/user/IVerifyToken.ts";
import MockVerification from "@/components/mock/MockVerification.vue";
import { useRouter } from "vue-router";
import { getRouteOnboardAccountType } from "@/views/Onboarding/OnboardingAccountType.vue";

const isAllowContinue = ref<boolean>(false);
const useMockVerification = ref<boolean>(true); // Use mock by default in development
const router = useRouter();

async function submitForm(): Promise<void> {
  // After verification, always go to account type selection (step 4)
  await router.push(getRouteOnboardAccountType());
}

const isDark = useDark();

async function updateReviewStatus() {
  return await personService.updateReviewStatus();
}

// Mock verification handlers
async function handleMockVerificationCompleted(payload: any) {
  console.log('Mock verification completed', payload);

  try {
    const result = await updateReviewStatus();
    console.log('updateReviewStatus result:', result);

    // Refresh person state to get updated verification status
    await personStore.getState();
    console.log('Person state after verification:', {
      profile: personStore.person?.profile,
      status: personStore.person?.profile?.status
    });

    isAllowContinue.value = true;

    // Auto-navigate to account type selection after 2 seconds
    setTimeout(async () => {
      await router.push(getRouteOnboardAccountType());
    }, 2000);
  } catch (error) {
    console.error('Error during mock verification completion:', error);
  }
}

function handleMockVerificationError(error: any) {
  console.error('Mock verification error', error);
}

/**
 * @param accessToken - access token that you generated on the backend in Step 2
 * @param applicantEmail - applicant email (not required)
 * @param applicantPhone - applicant phone, if available (not required)
 * @param applicantCountry - applicant country, if available (not required)
 * @param customI18nMessages - customized locale messages for current session (not required)
 */
function launchWebSdk(accessToken: any, applicantEmail: any, applicantPhone: any, applicantCountry: any) {

  let snsWebSdkInstance = snsWebSdk.init(
    accessToken,
    // token update callback, must return Promise
    // Access token expired
    // get a new one and pass it to the callback to re-initiate the WebSDK
    () => getSumsubToken()
  )
    .withConf({
      lang: "en", //language of WebSDK texts and comments (ISO 639-1 format)
      email: applicantEmail,
      phone: applicantPhone,
      theme: isDark.value ? "dark" : "light",
      country: applicantCountry
    })
    .withOptions({ addViewportTag: false, adaptIframeHeight: true })
    // see below what kind of messages WebSDK generates
    .on("idCheck.onStepCompleted", (payload) => {
      console.log("onStepCompleted", payload);
    })
    .on("idCheck.onApplicantStatusChanged", async (payload) => {
      console.log("onApplicantStatusChanged", payload);
      console.log('update review', await updateReviewStatus());
    })
    .on("idCheck.onError", (error) => {
      console.log("onError", error);
    })
    .build();

  // you are ready to go:
  // just launch the WebSDK by providing the container element for it
  snsWebSdkInstance.launch("#sumsub-websdk-container");
}

const personService = new PersonService();
const personStore = usePersonStore();
const person = personStore.person;

async function getSumsubToken(): Promise<string> {
  const tokenData = await personService.getVerifyToken();
  if (tokenData.hasOwnProperty('token')) {
    const data = tokenData as IVerifyToken;
    return data.token;
  }
  return '';
}

onMounted(async () => {
  // Only launch real Sumsub SDK if not using mock
  if (!useMockVerification.value) {
    const token = await getSumsubToken();
    try {
      launchWebSdk(token, person?.email, person?.phone, (person?.profile as any)?.country);
    } catch (e) {
      console.log(e)
    }
  } else {
    console.log('Using Mock Verification - Sumsub SDK not loaded');
  }
});

</script>

<template>
  <CardLayout
    title="Personal info"
    subtitle="Application for opening and maintaining a personal current account"
    heading="Verification"
    :max-steps="4"
    :current-step="3"
    :next="submitForm"
    :back="getRouteXDashboard()"
    :isAllowedNext="isAllowContinue"
  >

    <template #content class="w-full">
      <!-- Mock Verification Mode -->
      <div v-if="useMockVerification" class="w-full px-4 md:px-0">
        <MockVerification
          @completed="handleMockVerificationCompleted"
          @error="handleMockVerificationError"
        />
      </div>

      <!-- Real Sumsub SDK Mode -->
      <div v-else class="w-full" id="sumsub-websdk-container"></div>
    </template>
  </CardLayout>
</template>

<style scoped lang="scss">

</style>
